import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const SignupComplete: React.FC = () => {
  const location = useLocation();
  const email = location.state?.email || '';
  const hasReferralCoupon = location.state?.hasReferralCoupon || false;

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl border border-neutral-150 shadow-soft p-8 md:p-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-display font-bold text-neutral-900 mb-2">
            Registration Complete!
          </h1>
          <p className="text-neutral-600">
            Welcome to LiberaCure
          </p>
        </div>

        {/* Email Verification Notice */}
        <div className="bg-forest-50 border-2 border-forest-200 rounded-xl p-6 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Email Verification Required
              </h3>
              <p className="text-sm text-neutral-700 mb-3">
                A verification email has been sent to <span className="font-medium text-forest-400">{email}</span>.
              </p>
              <div className="bg-white rounded-lg p-4 border border-forest-100">
                <p className="text-sm text-neutral-600 mb-2">
                  <strong className="text-neutral-900">How to verify:</strong>
                </p>
                <ol className="text-sm text-neutral-600 space-y-1 ml-4 list-decimal">
                  <li>Check your email inbox</li>
                  <li>Open the email from LiberaCure</li>
                  <li>Click the "Verify Email" button</li>
                  <li>You can log in after verification is complete!</li>
                </ol>
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                Don't see the email? Check your <strong>spam folder</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Coupon Notice */}
        <div className="space-y-4 mb-8">
          {/* New Member Coupon */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Welcome Coupon Issued!
                </h3>
                <div className="bg-white rounded-lg p-4 border border-yellow-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-neutral-900">WELCOME10</span>
                    <span className="text-yellow-600 font-bold">$10 OFF</span>
                  </div>
                  <p className="text-xs text-neutral-600">
                    Valid on orders of $50 or more
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Coupon (conditional) */}
          {hasReferralCoupon && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Referral Bonus Coupon!
                  </h3>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-neutral-900">FRIENDNEW10</span>
                      <span className="text-purple-600 font-bold">$10 OFF</span>
                    </div>
                    <p className="text-xs text-neutral-600">
                      A special bonus from your referrer!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-neutral-50 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-neutral-900 mb-3">Next Steps</h3>
          <div className="space-y-2 text-sm text-neutral-600">
            <div className="flex items-start space-x-2">
              <span className="font-bold text-forest-400 flex-shrink-0">1.</span>
              <span>Complete email verification</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="font-bold text-forest-400 flex-shrink-0">2.</span>
              <span>Log in and check your coupons on My Page</span>
            </div>
            <div className="flex items-start space-x-2">
              <span className="font-bold text-forest-400 flex-shrink-0">3.</span>
              <span>Start shopping now!</span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/login"
            className="flex-1 bg-neutral-950 text-white py-3 px-6 rounded-full font-semibold hover:bg-neutral-800 transition-all duration-200 text-center cursor-pointer"
          >
            Log In
          </Link>
          <Link
            to="/shop"
            className="flex-1 bg-white border border-neutral-200 text-neutral-700 py-3 px-6 rounded-full font-semibold hover:bg-neutral-50 transition-all duration-200 text-center cursor-pointer"
          >
            Shop Now
          </Link>
        </div>

        {/* Help */}
        <p className="text-xs text-center text-neutral-500 mt-6">
          Having trouble? Contact our <Link to="/contact" className="text-forest-400 hover:underline">Support Team</Link>.
        </p>
      </div>
    </div>
  );
};

export default SignupComplete;
